'use client';
import { useEffect, useRef, useState } from 'react';

import Button from '@gds/Button/Button';
import { Typography } from '@gds/Typography/Typography';
import { useModal } from '@modal/Hooks/UseModal';
import { RichText } from '@page-builder/Components/RichText/RichText';
import { KenticoImage } from '@shared-components/KenticoImage/KenticoImage';
import Link from 'next/link';

import { ExtraUspItems } from './ExtraUspItems';
import { UspItemsProps } from './UspItems.entity';

import styles from './UspsItems.module.css';

export const UspsItems = ({
  children: RatingComponent,
  usps,
  showUsps,
  extraUsps,
}: UspItemsProps) => {
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const { openModal } = useModal();
  const extraUspsButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 80 && window.innerWidth >= 1024) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className={styles.uspsPadding} data-scrolled={isScrolling}>
      {showUsps && (
        <div className={styles.uspsWrapper}>
          <Typography variant="subheading1" className={styles.title}>
            {usps.heading}
            {usps.description && <span> {usps.description}</span>}
          </Typography>
          {usps.promoText && (
            <a href="#explanatory-video" className={styles.promoText}>
              <Typography variant="body2">{usps.promoText}</Typography>
            </a>
          )}
          <ul className={styles.usps}>
            {usps.uspItems.map(uspItem => {
              const { image, title, description, richDescription } = uspItem;

              return (
                <li className={styles.uspItem} key={description}>
                  <div className={styles.uspIcon}>
                    <KenticoImage
                      src={image[0]}
                      alt={title}
                      fill
                      style={{ objectFit: 'contain' }}
                      sizes="(max-width: 768px) 20vw, (max-width: 1200px) 33vw"
                    />
                  </div>
                  <Typography variant="subheading3">
                    {description}
                    {!description ? (
                      <RichText
                        richText={richDescription}
                        noPadding
                        className={styles.richTextDescription}
                      />
                    ) : null}
                  </Typography>
                </li>
              );
            })}
          </ul>

          {extraUsps ? (
            <Button
              className={styles.extraUspsButton}
              size="small"
              ref={extraUspsButtonRef}
              variant="link"
              onClick={() =>
                openModal(<ExtraUspItems items={extraUsps.uspItems} />, {
                  header: extraUsps.heading,
                  // return focus to the button that opened the modal, for proper accessibility
                  onClose: () => extraUspsButtonRef.current?.focus(),
                })
              }
            >
              Mehr dazu
            </Button>
          ) : null}

          {usps.uspLink[0]?.ctaText && (
            <Link
              href={usps.uspLink[0]?.link ?? '/'}
              key={usps.uspLink[0]?.ctaText}
              target="_blank"
            >
              <Typography className={styles.link} variant="buttonLink2">
                {usps.uspLink[0]?.ctaText}
              </Typography>
            </Link>
          )}

          {RatingComponent}
        </div>
      )}
    </div>
  );
};
